import { useMemo, useRef } from 'react';
// @mui
import Box from '@mui/material/Box';
import Collapse from '@mui/material/Collapse';
// hooks
import { useBoolean } from 'src/hooks/use-boolean';
// api
import { ReportType } from 'src/api';
// redux
import { useSelector } from 'src/redux/store';
// components
import { TableProps } from 'src/components/table';
//
import ReportsItem from './reports-item';
import ReportsItemSkeleton from './reports-item-skeleton';
import { useLocales } from '../../locales';
import { IReport } from '../../types/report';
import ReportsPanel from './reports-panel';

// ----------------------------------------------------------------------

type Props = {
  table: TableProps;
  data: IReport[];
  dataFiltered: IReport[];
};

export default function ReportsGridView({ table, data, dataFiltered }: Props) {
  const { selected, onSelectRow: onSelectItem } = table;
  const { t } = useLocales();
  const containerRef = useRef(null);

  const loading = useSelector((state) => state.report.loading[0]);

  const upload = useBoolean();

  const membersToggle = useBoolean();

  const membershipsToggle = useBoolean();

  const economyToggle = useBoolean();

  const classesToggle = useBoolean();

  const memberships = useMemo(
    () =>
      dataFiltered
        .filter((i) => i.type === ReportType.Membership)
        .sort((a, b) => a.name.localeCompare(b.name)),
    [dataFiltered]
  );

  const members = useMemo(
    () =>
      dataFiltered
        .filter((i) => i.type === ReportType.Member)
        .sort((a, b) => a.name.localeCompare(b.name)),
    [dataFiltered]
  );

  const economy = useMemo(
    () =>
      dataFiltered
        .filter((i) => i.type === ReportType.Economy)
        .sort((a, b) => a.name.localeCompare(b.name)),
    [dataFiltered]
  );

  const classes = useMemo(
    () =>
      dataFiltered
        .filter((i) => i.type === ReportType.Class)
        .sort((a, b) => a.name.localeCompare(b.name)),
    [dataFiltered]
  );

  return (
    <Box ref={containerRef}>
      <ReportsPanel
        title={t('Members')}
        subTitle={t(`{{count}} reports`, {
          count: data.filter((item) => item.type === ReportType.Member).length,
        })}
        onOpen={upload.onTrue}
        collapse={membersToggle.value}
        onCollapse={membersToggle.onToggle}
        loading={loading}
      />

      <Collapse in={!membersToggle.value} unmountOnExit>
        <Box
          display="grid"
          gridTemplateColumns={{
            xs: 'repeat(1, 1fr)',
            sm: 'repeat(2, 1fr)',
            md: 'repeat(3, 1fr)',
            lg: 'repeat(4, 1fr)',
          }}
          gap={3}
        >
          {loading && members.length === 0 ? (
            <ReportsItemSkeleton />
          ) : (
            members.map((file) => (
              <ReportsItem
                key={file.id}
                file={file}
                selected={selected.includes(file.id)}
                onSelect={() => onSelectItem(file.id)}
                sx={{ maxWidth: 'auto' }}
              />
            ))
          )}
        </Box>
      </Collapse>
      <ReportsPanel
        title={t('Memberships')}
        subTitle={t(`{{count}} reports`, {
          count: data.filter((item) => item.type === ReportType.Membership).length,
        })}
        sx={{ mt: 5 }}
        onOpen={upload.onTrue}
        collapse={membershipsToggle.value}
        onCollapse={membershipsToggle.onToggle}
        loading={loading}
      />

      <Collapse in={!membershipsToggle.value} unmountOnExit>
        <Box
          display="grid"
          gridTemplateColumns={{
            xs: 'repeat(1, 1fr)',
            sm: 'repeat(2, 1fr)',
            md: 'repeat(3, 1fr)',
            lg: 'repeat(4, 1fr)',
          }}
          gap={3}
        >
          {loading && memberships.length === 0 ? (
            <ReportsItemSkeleton />
          ) : (
            memberships.map((file) => (
              <ReportsItem
                key={file.id}
                file={file}
                selected={selected.includes(file.id)}
                onSelect={() => onSelectItem(file.id)}
                sx={{ maxWidth: 'auto' }}
              />
            ))
          )}
        </Box>
      </Collapse>

      <ReportsPanel
        title={t('Economy')}
        subTitle={t(`{{count}} reports`, {
          count: data.filter((item) => item.type === ReportType.Economy).length,
        })}
        sx={{ mt: 5 }}
        onOpen={upload.onTrue}
        collapse={economyToggle.value}
        onCollapse={economyToggle.onToggle}
        loading={loading}
      />

      <Collapse in={!economyToggle.value} unmountOnExit>
        <Box
          display="grid"
          gridTemplateColumns={{
            xs: 'repeat(1, 1fr)',
            sm: 'repeat(2, 1fr)',
            md: 'repeat(3, 1fr)',
            lg: 'repeat(4, 1fr)',
          }}
          gap={3}
        >
          {loading && economy.length === 0 ? (
            <ReportsItemSkeleton />
          ) : (
            economy.map((file) => (
              <ReportsItem
                key={file.id}
                file={file}
                selected={selected.includes(file.id)}
                onSelect={() => onSelectItem(file.id)}
                sx={{ maxWidth: 'auto' }}
              />
            ))
          )}
        </Box>
      </Collapse>

      <ReportsPanel
        title={t('Classes')}
        subTitle={t(`{{count}} reports`, {
          count: data.filter((item) => item.type === ReportType.Class).length,
        })}
        sx={{ mt: 5 }}
        onOpen={upload.onTrue}
        collapse={classesToggle.value}
        onCollapse={classesToggle.onToggle}
        loading={loading}
      />

      <Collapse in={!classesToggle.value} unmountOnExit>
        <Box
          display="grid"
          gridTemplateColumns={{
            xs: 'repeat(1, 1fr)',
            sm: 'repeat(2, 1fr)',
            md: 'repeat(3, 1fr)',
            lg: 'repeat(4, 1fr)',
          }}
          gap={3}
        >
          {loading && economy.length === 0 ? (
            <ReportsItemSkeleton />
          ) : (
            classes.map((file) => (
              <ReportsItem
                key={file.id}
                file={file}
                selected={selected.includes(file.id)}
                onSelect={() => onSelectItem(file.id)}
                sx={{ maxWidth: 'auto' }}
              />
            ))
          )}
        </Box>
      </Collapse>
    </Box>
  );
}
